import { Flame, Trophy, Sunrise, Sparkles, ArrowUpRight } from "lucide-react";

const topics = [
  {
    id: "passion",
    title: "The Passion Centered Person",
    subtitle: "How to Go from Having Just a Career to Having a Life",
    icon: Flame,
    span: "lg:col-span-6",
    description:
      "Gary brings the practical side of passion directly into your organization — where energy, attitude, and engagement determine performance. His signature 'beyond-burnout' framework re-engages teams who've gone routine, resistant, or quietly disconnected.",
    outcomes: [
      "The Passion Cycle™: Passion → Productivity → Profitability → Pleasure",
      "Build a referral mindset that grows the business",
      "Activate personal and team passion drivers",
      "Tools to increase both energy and results",
    ],
  },
  {
    id: "cause-for-applause-topic",
    title: "Cause for Applause",
    subtitle: "The Ultimate Appreciation Experience",
    icon: Sparkles,
    span: "lg:col-span-6",
    description:
      "A signature done-for-you appreciation experience that blends humor, recognition, and meaningful connection — strengthening relationships and re-energizing your team in one unforgettable night. Designed for dental practices, corporate teams, partner networks, and associations.",
    outcomes: [
      "Partners and clients feel celebrated",
      "Customized recognition for your team",
      "A high-energy, no-dull-moment experience",
      "Guests leave saying \"one of the best events ever\"",
    ],
  },
  {
    id: "life-on-the-ledge",
    title: "Life on the Ledge",
    subtitle: "The Power of Teamwork",
    icon: Sunrise,
    span: "lg:col-span-6",
    description:
      "When the stakes are high and the margin is thin, the team that holds the ledge wins. This high-energy session shows what it takes to build trust, share weight, and pull each other forward when it counts.",
    outcomes: [
      "How real teams move under pressure",
      "Trust, communication, and shared ownership",
      "Why people stay (or leave) — and what to do about it",
    ],
  },
  {
    id: "game-on",
    title: "Game On!",
    subtitle: "The Playbook for Taking Action",
    icon: Trophy,
    span: "lg:col-span-6",
    description:
      "Leadership isn't about position. It's about your ability to inspire people to want to follow you, work with you, and bring their best.",
    outcomes: [
      "5 fundamentals of high-performing teams",
      "Exit vs. Entrance Mentality",
      "Turn ideas into immediate action",
    ],
  },
];

export default function SpeakingTopics() {
  return (
    <section id="speaking" data-testid="speaking-section" className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-[#262626]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="section-num">02 / SIGNATURE KEYNOTES</span>
              <div className="divider-red" />
            </div>
            <h2 data-testid="speaking-heading" className="h-display text-5xl md:text-6xl lg:text-7xl">
              Four signature <br />
              <span className="text-[#EC1985]">messages.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/70 text-lg">
            Every message is customized to align with your audience, your culture, and your objectives — so your keynote becomes the most seamless and impactful part of the event.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {topics.map((t) => {
            const Icon = t.icon;
            return (
              <article
                key={t.id}
                data-testid={`topic-${t.id}`}
                className={`group surface p-8 md:p-10 ${t.span} hover:border-[#EC1985] transition-colors relative overflow-hidden`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="bg-[#EC1985]/10 border border-[#EC1985]/30 p-3">
                    <Icon size={24} className="text-[#EC1985]" />
                  </div>
                  <ArrowUpRight
                    size={28}
                    className="text-white/30 group-hover:text-[#EC1985] group-hover:rotate-45 transition-all"
                  />
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
                  {t.title}
                </h3>
                <div className="mt-2 text-sm uppercase tracking-[0.2em] text-[#EC1985]/90 font-bold">
                  {t.subtitle}
                </div>
                <p className="mt-5 text-white/70 leading-relaxed">{t.description}</p>
                <ul className="mt-6 space-y-2">
                  {t.outcomes.map((o, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <span className="text-[#EC1985] font-bold mt-1">→</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <a href="#book" data-testid="speaking-cta" className="btn-primary">
            Bring Gary to Your Event <ArrowUpRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}
